import {
  createPerfSession,
  startPerfSession,
  recordFrame,
  stopPerfSession,
  buildPerfReport
} from '../../application/perf/perfSession.js';
import {
  ARAM_SOFT_PERF_BUDGET,
  evaluateLiveFramesAgainstSoftBudget,
  evaluatePhaseTimingsAgainstSoftBudget,
  evaluateAllocAgainstSoftBudget
} from '../../domain/perf/aramSoftPerfBudget.js';
import { summarizeFrameSamples } from '../../domain/perf/perfStats.js';
import { buildSoftBudgetDashboard } from '../../application/perf/softBudgetDashboard.js';

function summarizePhaseSamples(phaseMsById) {
  const out = {};
  for (const [id, samples] of Object.entries(phaseMsById || {})) {
    out[id] = summarizeFrameSamples(samples);
  }
  return out;
}

function createAllocTracker() {
  return {
    startHeapMb: null,
    endHeapMb: null,
    peakHeapMb: null,
    sampleCount: 0
  };
}

function pushAllocSample(tracker, heapMb) {
  const n = Number(heapMb);
  if (!(n >= 0)) return;
  if (tracker.startHeapMb === null) tracker.startHeapMb = n;
  tracker.endHeapMb = n;
  tracker.peakHeapMb = tracker.peakHeapMb === null
    ? n
    : Math.max(tracker.peakHeapMb, n);
  tracker.sampleCount += 1;
}

function summarizeAlloc(tracker) {
  const start = tracker.startHeapMb;
  const end = tracker.endHeapMb;
  const peak = tracker.peakHeapMb;
  const sampleCount = tracker.sampleCount;
  const deltaMb = start !== null && end !== null ? end - start : null;
  return { startHeapMb: start, endHeapMb: end, peakHeapMb: peak, deltaMb, sampleCount };
}

/**
 * wirePerfDebug — Live agent harness: FPS, drawCalls, phase timings, alloc/GC, stress spawn.
 */
export function wirePerfDebug({
  globalObj = typeof window !== 'undefined' ? window : null,
  now = () => Date.now(),
  getRendererInfo = () => null,
  getCounts = () => ({}),
  spawnMinions = () => 0,
  spawnProjectiles = () => 0,
  castStorm = () => ({ ok: false }),
  getHeapMb = () => null
} = {}) {
  const session = createPerfSession({ now });
  const phaseMsById = {};
  let allocTracker = createAllocTracker();

  function clearPhaseSamples() {
    for (const key of Object.keys(phaseMsById)) delete phaseMsById[key];
  }

  function sampleExtras() {
    const info = getRendererInfo() || {};
    const render = info.render || {};
    const memory = info.memory || {};
    return {
      drawCalls: render.calls ?? null,
      geometries: memory.geometries ?? null,
      textures: memory.textures ?? null,
      entities: getCounts() || {},
      heapMB: getHeapMb(),
      phases: summarizePhaseSamples(phaseMsById),
      alloc: summarizeAlloc(allocTracker)
    };
  }

  const api = {
    softBudget: ARAM_SOFT_PERF_BUDGET,
    recording() {
      return !!session.recording;
    },
    start(opts = {}) {
      clearPhaseSamples();
      allocTracker = createAllocTracker();
      const started = startPerfSession(session, opts);
      pushAllocSample(allocTracker, getHeapMb());
      return started;
    },
    stop() {
      pushAllocSample(allocTracker, getHeapMb());
      return stopPerfSession(session);
    },
    sampleFrame(dt) {
      const ok = recordFrame(session, dt);
      if (session.recording) pushAllocSample(allocTracker, getHeapMb());
      return ok;
    },
    recordPhaseTimings(timingsMs = {}) {
      if (!session.recording) return false;
      for (const [id, ms] of Object.entries(timingsMs)) {
        const n = Number(ms);
        if (!(n >= 0) || !id) continue;
        if (!phaseMsById[id]) phaseMsById[id] = [];
        phaseMsById[id].push(n);
      }
      return true;
    },
    report() {
      return buildPerfReport(session, sampleExtras());
    },
    evaluateSoft() {
      const report = api.report();
      return {
        report,
        evaluation: evaluateLiveFramesAgainstSoftBudget(report.frames),
        phaseEvaluation: evaluatePhaseTimingsAgainstSoftBudget(report.phases),
        allocEvaluation: evaluateAllocAgainstSoftBudget(report.alloc)
      };
    },
    dashboard() {
      const soft = api.evaluateSoft();
      return buildSoftBudgetDashboard({
        evaluation: soft.evaluation,
        phaseEvaluation: soft.phaseEvaluation,
        allocEvaluation: soft.allocEvaluation,
        recording: api.recording()
      });
    },
    stress({ minions = 0, projectiles = 0, casts = 0 } = {}) {
      const m = spawnMinions(minions) || 0;
      const p = spawnProjectiles(projectiles) || 0;
      const castResults = [];
      for (let i = 0; i < Math.max(0, casts); i += 1) {
        castResults.push(castStorm());
      }
      return { minions: m, projectiles: p, casts: castResults.length, counts: getCounts() };
    }
  };

  if (globalObj) {
    globalObj.__debugPerf = api;
    globalObj.__debugSoftBudgetDashboard = () => api.dashboard();
  }
  return api;
}
